import React, { useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    IconButton,
    createStyles,
    Grid,
    makeStyles,
    Theme,
    Typography
} from "@material-ui/core";
import { Add, KeyboardBackspace } from '@material-ui/icons';
import { connect, useStore } from "react-redux";
import { Store } from "redux";
import { ApplicationState } from "../../redux/reducers";
import { avanzarSeccionGuiaWizard, IGuiaWizard, navegarSeccionGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { ICliente } from "../../entities/catalogos/ICliente";
import { ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { setGuiaServicios, setGuiaWizard } from "../../redux/actions/guiaActions";
import { loadCategoriasServicios } from "../../redux/actions/servicioAdicionalActions";
import { ICategoriaServicio } from "../../redux/types/servicioAdicionalType";
import ServicioAdicionalListaGuia from "./ServicioAdicionalListaGuia";
import { retornoDocumentosSeleccionadoInvalido } from "./retornoDocumentosGuiaHelpers";
import useGetServiciosGuia from "../../hooks/useGetServiciosGuia";
import useRecalcularTarifaGuia from "../../hooks/useRecalcularTarifaGuia";
import LoadingDialog from "../controls/LoadingDialog";
import NextButton from "../controls/NextButton";
import { traducirButton, traducirLabel, traducirMessage } from "../../blocks/traductor";
import { useSnackbar } from "notistack";
import swal from 'sweetalert2';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            boxShadow: "none",
            padding: theme.spacing(2)
        },
        actions: {
            width: "100%",
            textAlign: "right",
            paddingTop: "20px"
        },
        heading: {
            fontSize: "0.875rem",
            color: "#00653b",
            fontWeight: theme.typography.fontWeightBold,
            borderBottom: "2px solid #00653b"
        }
    }),
);

const esCategoriaIdValida = (id: number | undefined | null): id is number =>
    typeof id === "number" && id > 0;

const filtrarServiciosE3Utilizables = (
    servicios: ServicioAdicional[],
    categorias: ICategoriaServicio[]
): ServicioAdicional[] =>
    servicios.filter(
        s => esCategoriaIdValida(s.categoriaId) && categorias.some(c => c.id === s.categoriaId)
    );

interface StateProps {
    cliente: ICliente;
    guiaWizard: IGuiaWizard;
    guiaOrigen: IRecoleccion;
    guiaDestino: IRecoleccion;
    guiaServicios: ServicioAdicional[];
    categorias: ICategoriaServicio[];
}

interface DispatchProps {
    setGuiaWizard: typeof setGuiaWizard;
    setGuiaServicios: typeof setGuiaServicios;
    loadCategoriasServicios: typeof loadCategoriasServicios;
}

type Props = StateProps & DispatchProps;

const DatosServiciosGuia = ({
    cliente,
    guiaWizard,
    guiaOrigen,
    guiaDestino,
    guiaServicios,
    categorias,
    setGuiaWizard,
    setGuiaServicios,
    loadCategoriasServicios
}: Props) => {
    const classes = useStyles();
    const store = useStore() as Store<ApplicationState>;
    const { enqueueSnackbar } = useSnackbar();
    const { prepararServiciosGuia } = useGetServiciosGuia();
    const { recalcular } = useRecalcularTarifaGuia();
    const [servicios, setServicios] = useState<ServicioAdicional[]>(guiaServicios ?? []);
    const [loading, setLoading] = useState(false);
    const [cargado, setCargado] = useState(false);

    useEffect(() => {
        loadCategoriasServicios();
    }, []);

    useEffect(() => {
        cargarServicios();
    }, [guiaOrigen?.areaServicio?.id, guiaDestino?.areaServicio?.id, cliente?.id]);

    const cargarServicios = async () => {
        if (!cliente?.id || !guiaOrigen?.areaServicio?.id || !guiaDestino?.areaServicio?.id) {
            setServicios([]);
            setCargado(true);
            return;
        }

        setLoading(true);
        try {
            const { servicios: preparados, tipoServicioId, tipoEnvioId } = await prepararServiciosGuia(
                cliente,
                guiaOrigen,
                guiaDestino,
                guiaServicios
            );
            setServicios(preparados);
            setGuiaServicios(preparados.map(s => new ServicioAdicional(s)));
            if (
                (tipoServicioId && tipoServicioId !== guiaWizard.tipoServicioId)
                || (tipoEnvioId && tipoEnvioId !== guiaWizard.tipoEnvioId)
            ) {
                setGuiaWizard({
                    ...guiaWizard,
                    tipoServicioId: tipoServicioId || guiaWizard.tipoServicioId,
                    tipoEnvioId: tipoEnvioId || guiaWizard.tipoEnvioId
                });
            }
        } catch {
            setServicios([]);
        } finally {
            setLoading(false);
            setCargado(true);
        }
    };

    const seleccionarServicio = (servicio: ServicioAdicional) => {
        const copia = servicios.map(s => s.id !== servicio.id ? s : new ServicioAdicional(servicio));
        setServicios(copia);
        setGuiaServicios(copia.map(s => new ServicioAdicional(s)));
    };

    const validarEstructural = (): boolean => {
        let serviciosCheck = true;
        servicios.forEach(s => {
            if (s.seleccionado && !s.aplicaOrigen && !s.aplicaDestino) {
                const nombre = localStorage.getItem("idioma") === 'en-US'
                    ? (s.etiquetaServicioAdicionalDto?.nombre || s.nombre)
                    : s.nombre;
                swal.fire({
                    titleText: traducirMessage(11),
                    text: traducirMessage(291) + " " + nombre,
                    type: 'warning',
                });
                serviciosCheck = false;
            }
        });

        if (!serviciosCheck) {
            return false;
        }

        const serviciosE3Utilizables = filtrarServiciosE3Utilizables(servicios, categorias ?? []);
        const servicioRequerido = serviciosE3Utilizables.some(
            s => !!s.esRequerido && !s.seleccionado
        );
        if (servicioRequerido) {
            swal.fire({
                titleText: traducirMessage(11),
                text: traducirMessage(126),
                type: 'warning',
            });
            return false;
        }

        const sinCantidad = servicios.some(e => e.CantidadInvalida());
        const sinCosto = servicios.some(e => e.CostoInvalido());
        if (sinCantidad || sinCosto) {
            let error = "";
            if (sinCantidad) {
                error += traducirMessage(103) + " ";
            }
            if (sinCosto) {
                error += traducirMessage(99) + " ";
            }
            swal.fire({
                titleText: traducirMessage(11),
                text: error.trim(),
                type: 'warning',
            });
            return false;
        }

        const retornoInvalido = servicios.some(s => retornoDocumentosSeleccionadoInvalido(s));
        if (retornoInvalido) {
            enqueueSnackbar(traducirMessage(98), { variant: "error" });
            return false;
        }

        return true;
    };

    const handleRegresar = () => {
        setGuiaServicios(servicios.map(s => new ServicioAdicional(s)));
        const wizardActual = store.getState().guiaWizard;
        setGuiaWizard(navegarSeccionGuiaWizard(wizardActual, 2));
    };

    const handleContinuar = async () => {
        if (!validarEstructural()) {
            return;
        }
        const serviciosPersistidos = servicios.map(s => new ServicioAdicional(s));
        setGuiaServicios(serviciosPersistidos);

        setLoading(true);
        try {
            const result = await recalcular({ serviciosE3: serviciosPersistidos });
            if (!result.ok) {
                enqueueSnackbar(result.errorMessage || traducirMessage(11), { variant: "error" });
                return;
            }
            const wizardActual = store.getState().guiaWizard;
            setGuiaWizard(avanzarSeccionGuiaWizard(wizardActual, 4));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={classes.root}>
            {loading && <LoadingDialog titulo={'Procesando'} abierto={true} />}
            <Accordion expanded={true}>
                <AccordionSummary expandIcon={<Add />} aria-controls="servicios-guia-content" id="servicios-guia-header">
                    <Typography component={'span'} className={classes.heading}>
                        {traducirLabel(278)}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <Grid item sm={12}>
                            {cargado && (!guiaOrigen?.areaServicio?.id || !guiaDestino?.areaServicio?.id) ? (
                                <Typography variant="body2">
                                    {traducirMessage(130)}
                                </Typography>
                            ) : (
                                <ServicioAdicionalListaGuia
                                    servicios={servicios}
                                    onSelect={seleccionarServicio}
                                    agruparPorCategoria={true}
                                    documentosCliente={cliente?.documentos ?? []}
                                />
                            )}
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
            <Grid container className={classes.actions} style={{ backgroundColor: "white", padding: "10px" }}>
                <Grid item xs={6} style={{ textAlign: "left" }}>
                    {/*
                      NUEVA ETIQUETA REQUERIDA
                      Uso: botón Regresar Paso 3 → Paso 2 (Mercancía)
                      es-MX: Regresar
                      en-US: Back
                      tipo: btn_pantalla_XXX (consecutivo pendiente — Israel BD)
                      NO usar traducirButton(9) (= VOLVER A PÁGINA PRINCIPAL)
                      Hasta el INSERT: solo ícono (sin texto visible hardcodeado).
                    */}
                    <IconButton color="primary" onClick={handleRegresar}>
                        <KeyboardBackspace />
                    </IconButton>
                </Grid>
                <Grid item xs={6} style={{ textAlign: "right" }}>
                    <NextButton nextStep={handleContinuar} label={traducirButton(7)} />
                </Grid>
            </Grid>
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    cliente: state.cliente,
    guiaWizard: state.guiaWizard,
    guiaOrigen: state.guiaOrigen,
    guiaDestino: state.guiaDestino,
    guiaServicios: state.guiaServicios,
    categorias: state.categoriasServicios ?? []
});

const mapDispatchToProps = {
    setGuiaWizard,
    setGuiaServicios,
    loadCategoriasServicios
};

export default connect(mapStateToProps, mapDispatchToProps)(DatosServiciosGuia);
